/**
 * UAPC Anytime - Vendor Reports Page
 * @module VendorReports
 * @author Sohail - Backend Developer
 * @description Sales and inventory reports
 * @version 1.0.0
 * Git: Sohail <sohail@linesoft.com> - Reporting implementation
 */

import React, { useState, useEffect } from 'react';
import { DollarSign, Package, AlertTriangle, TrendingUp } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import VendorLayout from '../../components/VendorLayout';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VendorReports = () => {
  const [salesData, setSalesData] = useState(null);
  const [inventoryData, setInventoryData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const [salesRes, inventoryRes] = await Promise.all([
        axios.get(`${API_URL}/api/vendor/reports/sales`),
        axios.get(`${API_URL}/api/vendor/reports/inventory`)
      ]);
      setSalesData(salesRes.data);
      setInventoryData(inventoryRes.data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <VendorLayout>
        <div className="p-8">
          <div className="animate-pulse space-y-6">
            <div className="h-10 bg-slate-200 dark:bg-slate-800 w-48" />
            <div className="grid grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-32 bg-slate-200 dark:bg-slate-800" />
              ))}
            </div>
          </div>
        </div>
      </VendorLayout>
    );
  }

  return (
    <VendorLayout>
      <div className="p-8" data-testid="vendor-reports">
        <h1 className="font-heading font-black text-3xl text-slate-900 dark:text-white uppercase tracking-tight mb-8">
          Reports
        </h1>

        <Tabs defaultValue="sales">
          <TabsList className="mb-8">
            <TabsTrigger value="sales" className="font-heading uppercase" data-testid="tab-sales">Sales</TabsTrigger>
            <TabsTrigger value="inventory" className="font-heading uppercase" data-testid="tab-inventory">Inventory</TabsTrigger>
          </TabsList>

          <TabsContent value="sales">
            {/* Sales Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-green-600" />
                  </div>
                  <span className="font-mono text-xs text-slate-500 uppercase">Total Sales</span>
                </div>
                <div className="font-heading font-black text-4xl text-slate-900 dark:text-white" data-testid="total-sales">
                  ${salesData?.total_sales?.toFixed(2) || '0.00'}
                </div>
              </div>

              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <Package className="w-6 h-6 text-blue-600" />
                  </div>
                  <span className="font-mono text-xs text-slate-500 uppercase">Orders</span>
                </div>
                <div className="font-heading font-black text-4xl text-slate-900 dark:text-white" data-testid="order-count">
                  {salesData?.order_count || 0}
                </div>
              </div>

              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-purple-600" />
                  </div>
                  <span className="font-mono text-xs text-slate-500 uppercase">Avg Order</span>
                </div>
                <div className="font-heading font-black text-4xl text-slate-900 dark:text-white">
                  ${salesData?.order_count > 0 ? (salesData.total_sales / salesData.order_count).toFixed(2) : '0.00'}
                </div>
              </div>
            </div>

            {/* Recent Orders */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
              <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white uppercase mb-6">
                Recent Orders
              </h3>
              {salesData?.orders?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200 dark:border-slate-700">
                        <th className="pb-3 text-left text-xs font-heading font-bold uppercase text-slate-500">Order ID</th>
                        <th className="pb-3 text-left text-xs font-heading font-bold uppercase text-slate-500">Date</th>
                        <th className="pb-3 text-left text-xs font-heading font-bold uppercase text-slate-500">Items</th>
                        <th className="pb-3 text-right text-xs font-heading font-bold uppercase text-slate-500">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salesData.orders.slice(0, 10).map((order, index) => (
                        <tr key={order.order_id} className="border-b border-slate-100 dark:border-slate-800">
                          <td className="py-3 font-mono text-sm">{order.order_id.slice(0, 8)}</td>
                          <td className="py-3 text-slate-600 dark:text-slate-400">
                            {new Date(order.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-3 text-slate-600 dark:text-slate-400">{order.items.length}</td>
                          <td className="py-3 text-right font-heading font-bold text-blue-600">
                            ${order.total.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No orders yet</p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="inventory">
            {/* Inventory Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <Package className="w-6 h-6 text-blue-600" />
                  </div>
                  <span className="font-mono text-xs text-slate-500 uppercase">Total Parts</span>
                </div>
                <div className="font-heading font-black text-4xl text-slate-900 dark:text-white" data-testid="inv-total-parts">
                  {inventoryData?.total_parts || 0}
                </div>
              </div>

              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-green-600" />
                  </div>
                  <span className="font-mono text-xs text-slate-500 uppercase">Total Value</span>
                </div>
                <div className="font-heading font-black text-4xl text-slate-900 dark:text-white" data-testid="inv-total-value">
                  ${inventoryData?.total_value?.toFixed(2) || '0.00'}
                </div>
              </div>

              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-orange-600" />
                  </div>
                  <span className="font-mono text-xs text-slate-500 uppercase">Low Stock</span>
                </div>
                <div className="font-heading font-black text-4xl text-slate-900 dark:text-white" data-testid="inv-low-stock">
                  {inventoryData?.low_stock?.length || 0}
                </div>
              </div>

              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                    <Package className="w-6 h-6 text-red-600" />
                  </div>
                  <span className="font-mono text-xs text-slate-500 uppercase">Out of Stock</span>
                </div>
                <div className="font-heading font-black text-4xl text-slate-900 dark:text-white" data-testid="inv-out-of-stock">
                  {inventoryData?.out_of_stock?.length || 0}
                </div>
              </div>
            </div>

            {/* Low Stock Items */}
            {inventoryData?.low_stock?.length > 0 && (
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
                <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white uppercase mb-6">
                  Low Stock Items
                </h3>
                <div className="space-y-3">
                  {inventoryData.low_stock.map((part, index) => (
                    <div key={part.id} className="flex items-center justify-between py-3 border-b border-slate-100 dark:border-slate-800 last:border-0">
                      <div>
                        <span className="font-mono text-xs text-slate-500">{part.part_number}</span>
                        <span className="mx-2">-</span>
                        <span className="font-medium text-slate-900 dark:text-white">{part.name}</span>
                      </div>
                      <span className={`px-2 py-1 text-xs font-bold ${
                        part.quantity === 0 ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800'
                      }`}>
                        {part.quantity} left
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </VendorLayout>
  );
};

export default VendorReports;
